import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { commonButtonProps } from "../utils/styles";

interface BreadcrumbItem {
  label: string;
  action?: () => void;
  path?: string; // If undefined, treat as current page
}

interface ButtonConfig {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
}

interface PageHeaderProps {
  mainTitle?: string;
  title?: string;
  breadcrumbs?: BreadcrumbItem[];
  button?: ButtonConfig | null;
  buttons?: ButtonConfig[]; // Support multiple buttons
  backNavigationTo?: string;
  backNavigationEnabled?: boolean;
}

const NavigationHeader: React.FC<PageHeaderProps> = ({
  mainTitle,
  title,
  breadcrumbs,
  button,
  buttons,
  backNavigationTo,
  backNavigationEnabled = true,
}) => {
  const navigate = useNavigate();

  // Use buttons array if provided, otherwise fall back to single button
  const buttonConfigs = buttons || (button ? [button] : []);

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          {mainTitle && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  fontSize: "1rem",
                  color: "primary.main",
                }}
              >
                {mainTitle}
              </Typography>
            </Box>
          )}
          {title && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={() =>
                  backNavigationTo ? navigate(backNavigationTo) : navigate(-1)
                }
                size="small"
                sx={{
                  mr: 1,
                  color: "primary.main",
                  display: backNavigationEnabled ? "block" : "none",
                  fontSize: "0.65rem",
                }}
              >
                <ArrowBack sx={{ fontSize: "0.9rem" }} />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  color: "primary.main",
                }}
              >
                {title}
              </Typography>
            </Box>
          )}
          {breadcrumbs && (
            <Breadcrumbs separator="›" sx={{ fontSize: "0.65rem" }}>
              {breadcrumbs?.map((item, index) =>
                item.path || item.action ? (
                  <Link
                    key={index}
                    color="inherit"
                    onClick={() => {
                      if (item.action) {
                        item.action();
                      } else {
                        navigate(item.path!);
                      }
                    }}
                    sx={{ cursor: "pointer", fontSize: "0.65rem" }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <Typography
                    key={index}
                    color="text.primary"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    {item.label}
                  </Typography>
                )
              )}
            </Breadcrumbs>
          )}
        </Box>
        {buttonConfigs.length > 0 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {buttonConfigs.map((btn, index) => (
              <Button
                key={index}
                variant={btn.variant || "contained"}
                color={btn.color || "primary"}
                onClick={btn.onClick}
                {...commonButtonProps}
                startIcon={btn.icon}
              >
                {btn.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default NavigationHeader;
